import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from "react-router-dom";
import useModel3DViewer from "../Customiz3r/Editor3D/useModel3DViewer.js";
import { showErrorAlert } from "../../components/HelperAlerts/HelpersAlerts.jsx";
import axios from "axios";
import { getThemeContext } from "../../components/providers/ThemeProvider.jsx";
import { useAuthContext } from "../../components/providers/AuthProvider.jsx";
import ModelViewerShell from "../../components/Viewer3D/ModelViewerShell.jsx";
import CustomOrderRequestModal from "../Customiz3r/components/CustomOrderRequestModal.jsx";
import { editorPathForModel } from "../../products/registry";
import {
    ArrowLeftIcon,
    PencilSquareIcon,
    ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

// ── CTA captación ────────────────────────────────────────────────────────────
const PublicCtaBanner = () => (
    <div className="fixed bottom-20 right-3 z-40 lg:bottom-24 lg:right-5">
        <div className="glassCard rounded-xl px-4 py-3 flex flex-col gap-2 shadow-lg w-48">
            <p className="text-sm font-semibold text-base-content leading-tight">
                ¿Te interesa este diseño?
            </p>
            <Link to="/register" className="btn btn-primary btn-sm w-full">
                Regístrate y pídelo
            </Link>
            <Link to="/contact" className="btn btn-ghost btn-sm w-full">
                Contactar
            </Link>
        </div>
    </div>
);

const Public3dViewer = () => {
    const theme = getThemeContext();
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuth } = useAuthContext();

    const [modelMeta, setModelMeta] = useState(null);
    // El modelo completo, solo si quien abre el enlace es su dueño: con él se
    // le ofrece editarlo y pedirlo, igual que en su propio visor.
    const [ownerModel, setOwnerModel] = useState(null);
    const [requestOpen, setRequestOpen] = useState(false);

    const handleLoadError = useCallback((err, { setError, setLoading }) => {
        console.error('Error cargando modelo:', err);
        setError(`Token inválido, expirado o se excedió el número de usos: ${err.message || 'Error desconocido'}`);
        setLoading(false);
        showErrorAlert({
            text: "Token inválido, expirado o se excedió el número de usos.",
            theme,
        });
    }, [theme]);

    const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;
    const viewer = useModel3DViewer({
        glbUrl: token ? `${API_BASE_URL}public-viewer/${token}/` : null,
        bottomLight: true,
        onLoadError: handleLoadError,
    });

    // ── Fetch model metadata ─────────────────────────────────────────────────
    // Se repite al cambiar la sesión: la sesión se restaura después de montar
    // la página, y hasta entonces el backend no sabe si eres el dueño.
    useEffect(() => {
        if (!token) return;
        axios.get(`${API_BASE_URL}public-viewer-metadata/${token}/`)
            .then(res => setModelMeta(res.data))
            .catch(err => console.warn('No se pudo obtener metadatos del modelo:', err));
    }, [token, API_BASE_URL, isAuth]);

    useEffect(() => {
        const modelId = modelMeta?.is_owner ? modelMeta.model_id : null;
        if (!modelId) {
            setOwnerModel(null);
            return;
        }
        axios.get(`customized-keyrings/${modelId}/`)
            .then(res => setOwnerModel(res.data))
            .catch(err => console.warn('No se pudo obtener el modelo del dueño:', err));
    }, [modelMeta]);

    const isOwner = Boolean(ownerModel);
    const ownerActions = isOwner ? [
        { key: 'edit', icon: PencilSquareIcon, label: 'Editar en Customiz3r', mobileLabel: 'Editar', tip: 'Editar el diseño en Customiz3r', onClick: () => navigate(editorPathForModel(ownerModel)) },
        { key: 'order', icon: ClipboardDocumentCheckIcon, label: 'Pedir este diseño', mobileLabel: 'Pedir', tip: 'Pedir que te fabriquemos este diseño', onClick: () => setRequestOpen(true), isPrimary: true },
    ] : [];

    return (
        <ModelViewerShell
            viewer={viewer}
            subtitle="Visor Público 3D"
            loadingTitle="Cargando modelo 3D público"
            errorBack={{ label: 'Volver al inicio', onClick: () => navigate('/') }}
            back={{ label: 'Inicio', tip: 'Volver al inicio', icon: ArrowLeftIcon, onClick: () => navigate('/') }}
            modelName={modelMeta?.name || 'Modelo 3D'}
            formData={modelMeta?.form2d_data}
            // El dueño puede tocar los colores de lo suyo aunque el enlace no
            // lo permita a los demás.
            canEditColors={isOwner || modelMeta?.allow_color_edit === true}
            actions={ownerActions}
            // Quien ya tiene cuenta no necesita que le pidan registrarse.
            aside={isAuth ? null : <PublicCtaBanner />}
        >
            {requestOpen && ownerModel && (
                <CustomOrderRequestModal keyring={ownerModel} onClose={() => setRequestOpen(false)} />
            )}
        </ModelViewerShell>
    );
};

export default Public3dViewer;
